import { Fragment, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { formatBillingDate } from './billingDate';
import { AgingBadge } from './AgingBadge';
import {
  DueCell,
  InvoiceAmount,
  KindBadge,
  LifecycleBadge,
  OutstandingAmount,
  SentDateCell,
} from './InvoiceCells';
import { InvoiceRowActions, type InvoiceRowActionsProps } from './InvoiceRowActions';
import { SendOutcomeBadge } from './SendOutcomeBadge';
import type { BillingInvoiceRow, InvoiceColumns } from './invoiceRow';

/**
 * The invoice list, shared by the /invoices workspace and the job Billing tab.
 *
 * One component with a `columns` set rather than two components: the workspace
 * shows client and job columns, the job-scoped tab omits them because it is
 * already on that job. Everything else about a row is identical, and building
 * it twice is the mistake this module is shaped to avoid.
 */

export type InvoiceRowActionHandlers = Omit<InvoiceRowActionsProps, 'invoice'>;

/**
 * Generic over the row type so a caller's callbacks keep ITS row type rather
 * than being widened to BillingInvoiceRow. The workspace's handlers need
 * `jobId` and `invoiceId` off a WorkspaceInvoiceRow, and the job tab's need
 * the fields its own adapter produced.
 */
export interface InvoiceTableProps<T extends BillingInvoiceRow = BillingInvoiceRow> {
  rows: T[];
  columns: InvoiceColumns;
  /** Per-row handlers. Called for each row so the caller can close over it. */
  actionsFor?: (row: T) => InvoiceRowActionHandlers;
  onRowClick?: (row: T) => void;
  /** Rendered above a row — stage 3's 30/60/90 group headers use this. */
  groupHeaderFor?: (row: T, index: number) => ReactNode;
  /** Rendered in the notes column when `columns.notes` is set. */
  notesCellFor?: (row: T) => ReactNode;
  emptyState?: ReactNode;
  className?: string;
}

export function InvoiceTable<T extends BillingInvoiceRow>({
  rows,
  columns,
  actionsFor,
  onRowClick,
  groupHeaderFor,
  notesCellFor,
  emptyState,
  className,
}: InvoiceTableProps<T>) {
  if (rows.length === 0 && emptyState) return <>{emptyState}</>;

  const columnCount =
    5 + // number, kind, due, amount, status
    (columns.client ? 1 : 0) +
    (columns.job ? 1 : 0) +
    (columns.aging ? 1 : 0) +
    (columns.collected ? 1 : 0) +
    (columns.sentDate ? 1 : 0) +
    (columns.paidDate ? 1 : 0) +
    (columns.notes ? 1 : 0) +
    1; // actions

  // The minimum width follows the column set rather than being one number for
  // both callers: the workspace carries client and job columns of free text and
  // needs the room, while the job-scoped tab is already on its job and would
  // otherwise scroll for space it does not use.
  const minWidth = columns.client || columns.job ? 'min-w-[58rem]' : 'min-w-[46rem]';

  return (
    <div className={cn('overflow-x-auto rounded-lg border bg-white', className)}>
      <table className={cn('w-full text-sm', minWidth)}>
        <thead>
          <tr className="border-b bg-gray-50 text-left text-gray-500">
            <th className="whitespace-nowrap px-4 py-3 font-medium">Invoice #</th>
            {columns.client && <th className="px-4 py-3 font-medium">Client</th>}
            {columns.job && <th className="px-4 py-3 font-medium">Job</th>}
            <th className="px-4 py-3 font-medium">Kind</th>
            {/*
              The SENT date, in what used to be the invoice-date column. The
              date an invoice was raised in this system appears on no document
              and decides nothing — terms run from the day it went out, which is
              also the date printed on the customer's copy (NEW-235). So there
              is one date column here, and it says which date it is.
            */}
            {columns.sentDate && <th className="whitespace-nowrap px-4 py-3 font-medium">Sent</th>}
            {/* Age belongs beside the date it counts from, not ten columns away. */}
            {columns.aging && <th className="px-4 py-3 font-medium">Age</th>}
            <th className="whitespace-nowrap px-4 py-3 font-medium">Due</th>
            <th className="whitespace-nowrap px-4 py-3 text-right font-medium">Amount</th>
            {columns.collected && (
              <th className="whitespace-nowrap px-4 py-3 text-right font-medium">Outstanding</th>
            )}
            <th className="px-4 py-3 font-medium">Status</th>
            {columns.paidDate && <th className="whitespace-nowrap px-4 py-3 font-medium">Paid</th>}
            {columns.notes && <th className="px-4 py-3 font-medium">Notes</th>}
            <th className="w-12 px-2 py-3" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const header = groupHeaderFor?.(row, index);
            const actions = actionsFor?.(row);
            const voided = row.lifecycle === 'voided';
            const strike = voided ? 'line-through' : '';

            return (
              <Fragment key={row.invoiceId}>
                {header && (
                  <tr>
                    <td colSpan={columnCount} className="border-b bg-gray-50/70 px-4 py-2">
                      {header}
                    </td>
                  </tr>
                )}
                <tr
                  className={cn(
                    'border-b last:border-b-0',
                    voided && 'opacity-50',
                    row.kind === 'final' && !voided && 'bg-slate-50',
                    onRowClick && 'cursor-pointer hover:bg-gray-50',
                  )}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                >
                  <td className={cn('whitespace-nowrap px-4 py-3 font-mono', strike)}>
                    {row.invoiceNumber}
                  </td>
                  {columns.client && (
                    <td className="max-w-[12rem] truncate px-4 py-3">{row.clientName ?? '—'}</td>
                  )}
                  {columns.job && (
                    <td className="max-w-[14rem] px-4 py-3">
                      <span className="font-mono">{row.jobCode ?? '—'}</span>
                      {row.jobDescription && (
                        <span className="block truncate text-xs text-gray-500">
                          {row.jobDescription}
                        </span>
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <KindBadge kind={row.kind} isCash={row.isCash} />
                  </td>
                  {columns.sentDate && (
                    <td
                      className={cn('whitespace-nowrap px-2 py-3 text-gray-600', strike)}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <SentDateCell row={row} onSetSentDate={actions?.onSetSentDate} />
                    </td>
                  )}
                  {columns.aging && (
                    <td className="px-4 py-3">
                      <AgingBadge
                        bucket={row.agingBucket}
                        daysOutstanding={row.daysOutstanding}
                        isOverdue={row.isOverdue}
                      />
                    </td>
                  )}
                  <td className={cn('whitespace-nowrap px-4 py-3', strike)}>
                    <DueCell row={row} />
                  </td>
                  <td className={cn('px-4 py-3 text-right', strike)}>
                    <InvoiceAmount row={row} onAmend={actions?.onAmendAmount as (() => void) | undefined} />
                  </td>
                  {columns.collected && (
                    <td className={cn('px-4 py-3 text-right', strike)}>
                      <OutstandingAmount row={row} />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    {/*
                      Two badges, so the cell needs the wrapper the single one
                      did not. The bounce badge shares this cell rather than
                      taking a column: it is blank on every healthy row, and a
                      column of its own would also have to be counted into
                      `columnCount` above or every group header in the Sent
                      stage would misalign.
                    */}
                    <div className="flex items-center gap-1.5">
                      {/* Server-derived, never recomputed here (spec D6). */}
                      <LifecycleBadge lifecycle={row.lifecycle} />
                      {columns.sendOutcome && (
                        <SendOutcomeBadge state={row.lastSendState} detail={row.lastSendDetail} />
                      )}
                    </div>
                  </td>
                  {columns.paidDate && (
                    <td className="whitespace-nowrap px-4 py-3 text-gray-600">
                      {row.paidAt ? formatBillingDate(row.paidAt) : '—'}
                    </td>
                  )}
                  {columns.notes && (
                    <td className="px-2 py-3" onClick={(e) => e.stopPropagation()}>
                      {notesCellFor?.(row)}
                    </td>
                  )}
                  <td className="px-2 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    {actions && <InvoiceRowActions invoice={row} {...actions} />}
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

